import { cn } from '@sohanemon/utils';
import { Iconify } from '@sohanemon/utils/components';
import { useCopyToClipboard } from '@sohanemon/utils/hooks';
import { m } from '@sohanscript/i18n';
import type * as React from 'react';
import { Button } from './button';

export type CopyButtonProps = React.ComponentProps<'button'> & {
	/** Text placed on the clipboard. */
	text: string;
	/** Accessible name of the copied item; forwarded to `onCopied`. */
	label?: string;
	/** Called after the text reached the clipboard. */
	onCopied?: (label?: string) => void;
	/** Called when the clipboard is unavailable. */
	onCopyFailed?: () => void;
	/** Custom content; defaults to the copy/copied label. */
	children?: React.ReactNode;
	/** Do not render the icon. */
	noIcon?: boolean;
};

/**
 * Copy-to-clipboard button. Owns the clipboard access and the copied state;
 * consumers style it and report the result.
 */
export function CopyButton({
	text,
	label,
	onCopied,
	onCopyFailed,
	noIcon,
	className,
	children,
	...props
}: CopyButtonProps) {
	const { copy, isCopied } = useCopyToClipboard({ timeout: 2000 });

	return (
		<Button
			type="button"
			aria-label={isCopied ? m.copy_aria_copied() : (label ?? m.copy_aria())}
			onClick={() => {
				if (!navigator.clipboard?.writeText) {
					onCopyFailed?.();
					return;
				}
				copy(text);
				onCopied?.(label);
			}}
			variant="nav"
			size="xs"
			className={cn('shrink-0 not-hover:opacity-60 h-6', className)}
			{...props}
		>
			{noIcon ? null : (
				<Iconify
					icon={isCopied ? 'lucide:check' : 'lucide:copy'}
					className="size-3.5"
					aria-hidden="true"
				/>
			)}
			{children ?? (isCopied ? m.copy_copied() : m.copy_copy())}
		</Button>
	);
}
